import _ from "lodash";
import React, { useState, useMemo, memo, useCallback } from "react";

import { useI18n } from "Hooks";

import { Checkbox, Avatar, EmptyState, IconButton } from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import classes from "./GroupedList.scss";
import classNames from "classnames";
import { CaretOpenedFilled, CaretClosedFilled } from "@toddle-edu/ds-icons";
import SkeletonLoader from "UIComponents/SkeletonLoader";

const CHECKBOX_STATE = {
  INDETERMINATE: "INDETERMINATE",
  DETERMINATE: "DETERMINATE",
  EMPTY: "EMPTY",
};

const USER_TYPE_COUNT_FIELDS = {
  STAFF: "staffCount",
  STUDENT: "studentCount",
  FAMILY_MEMBER: "familyMemberCount",
};

const USER_TYPE_FIELDS = {
  STAFF: "collaborator",
  STUDENT: "students",
  FAMILY_MEMBER: "familyMembers",
};

const PARTICIPANT_USER_TYPES = {
  STAFF: "common:staff_member",
  STUDENT: "common:student",
  FAMILY_MEMBER: "common:family_member",
};

const EmptyStateComp = () => {
  const { t } = useI18n(["common"]);
  return (
    <EmptyState
      dsVersion="2.0"
      illustration={NoSearchResultsIllustration}
      title={t("common:noResultHeading")}
      subtitle={t("common:no_items_matching_your_search")}
    />
  );
};

const OptionsList = ({
  viewOnly,
  options,
  groupId,
  selectedItemListSet,
  setSelectedItemsList,
  selectedUserTypes,
  testId,
  coursesRef,
  coursesSetRef,
  countMapping,
  loadingCount,
  isNotMultiNodeCount = true,
}) => {
  const { t } = useI18n();
  const componentTestId = testId ? `${testId}-optionsList` : undefined;
  const onClickItem = ({ item, isChecked }) => {
    if (isChecked) {
      setSelectedItemsList(prev => _.filter(prev, ({ id }) => id != item.id));
    } else {
      setSelectedItemsList((prevSelectedItems = []) => [
        ...prevSelectedItems,
        item,
      ]);
    }
  };

  const recipientSubItemClasses = classNames(classes.recipientSubItem, {
    [classes.recipientSubItemV3]: true,
  });
  const createItemRef = useCallback(
    index => element => {
      if (coursesSetRef) {
        coursesSetRef(index, element);
      } else {
        // Fallback to direct assignment if setRef not available
        if (element) {
          coursesRef.current[index] = element;
        } else {
          delete coursesRef.current[index];
        }
      }
    },
    [coursesSetRef, coursesRef]
  );

  return _.map(options, (item, idx) => {
    const { id, name, title } = item;
    const label = title ?? name;

    const isChecked = selectedItemListSet.has(id);

    return (
      <div
        key={`${groupId}-${id}`}
        className={recipientSubItemClasses}
        onClick={
          viewOnly
            ? null
            : () => {
                onClickItem({ item, isChecked });
              }
        }
        ref={createItemRef(idx)}
        data-id={id}
      >
        <div className={classes.leftContainer}>
          <Avatar
            dsVersion="2.0"
            id={groupId}
            name={label}
            size="medium"
            shape="square"
          />
          <div className={classes.itemDetail}>
            <div className={classNames("text-body", classes.itemLabel)}>
              {label}
            </div>
            <div className={classes.itemCountRow}>
              {loadingCount && !_.has(countMapping, id) ? (
                <SkeletonLoader height="16px" width="360px" />
              ) : (
                _.map(selectedUserTypes, (userType, index) => {
                  const userTypeCount = isNotMultiNodeCount
                    ? _.get(item, USER_TYPE_COUNT_FIELDS[userType])
                    : _.get(
                        countMapping,
                        `${id}.${USER_TYPE_FIELDS[userType]}.totalCount`,
                        0
                      );

                  return (
                    <div
                      className={classNames(
                        "text-body-s",
                        classes.userTypeCount
                      )}
                      key={`${id}-${userType}`}
                    >
                      {index ? ", " : ""}
                      {`${userTypeCount} ${t(PARTICIPANT_USER_TYPES[userType], {
                        count: userTypeCount,
                      })}`}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <div className={classes.rightContainer}>
          {viewOnly ? null : (
            <Checkbox
              isChecked={isChecked}
              testId={`${componentTestId}-${id}`}
            />
          )}
        </div>
      </div>
    );
  });
};

const MemoisedOptionList = memo(OptionsList);

const GroupedList = ({
  viewOnly,
  itemList,
  selectedItemsList,
  setSelectedItemsList,
  selectedUserTypes,
  testId,
  coursesRef = { current: [] },
  coursesSetRef,
  countMapping,
  loadingCount,
  isNotMultiNodeCount = true,
}) => {
  const componentTestId = testId ? `${testId}-groupedList` : undefined;
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const handleToggleCollapseList = (e, groupId) => {
    e.stopPropagation();
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // NOTE: The hack to improve performance by creating Set whenever itemList changes
  // By this we would not need to compare by id in the loop, for every item.
  // Ideally the state should be stored in a Set
  const selectedItemListSet = useMemo(
    () => new Set(_.map(selectedItemsList, ({ id }) => id)),
    [selectedItemsList]
  );

  const selectedGroupStatus = useMemo(() => {
    const groupStatus = {};

    _.forEach(itemList, ({ options, groupId }) => {
      let checkedCount = 0;

      _.forEach(options, ({ id }) => {
        if (selectedItemListSet.has(id)) checkedCount++;
      });

      if (checkedCount === 0) groupStatus[groupId] = CHECKBOX_STATE.EMPTY;
      else if (checkedCount === options.length) {
        groupStatus[groupId] = CHECKBOX_STATE.DETERMINATE;
      } else groupStatus[groupId] = CHECKBOX_STATE.INDETERMINATE;
    });

    return groupStatus;
  }, [selectedItemListSet, itemList]);

  const handleOnGroupClick = ({ groupId, selectedGroupOptions }) => {
    const isAllOptionsSelected =
      _.get(selectedGroupStatus, groupId) === CHECKBOX_STATE.DETERMINATE;

    if (isAllOptionsSelected) {
      const selectedGroupOptionsIdsSet = new Set(
        _.map(selectedGroupOptions, "id")
      );

      setSelectedItemsList(prevSelectedItems => {
        return _.filter(
          prevSelectedItems,
          ({ id }) => !selectedGroupOptionsIdsSet.has(id)
        );
      });
    } else {
      // by default prevSelectedItemsList is undefined
      setSelectedItemsList((prevSelectedItems = []) => {
        const combinedItems = _.concat(prevSelectedItems, selectedGroupOptions);
        return _.uniqBy(combinedItems, "id");
      });
    }
  };

  const labelClasses = classNames("heading-6", {
    "text-body": true,
    [classes.weight600]: true,
    [classes.collapseButton]: true,
  });

  const labelContainerClasses = classNames(
    classes.recipientItemLabelContainer,
    {
      [classes.recipientItemLabelContainerV3]: true,
    }
  );

  const groupContainerClasses = classNames(classes.recipientItem, {
    [classes.groupContainer]: true,
  });

  return (
    <div className={classes.recipientList}>
      {_.isEmpty(itemList) && !viewOnly ? (
        <EmptyStateComp />
      ) : (
        _.map(itemList, ({ label, groupId, options }, idx) => {
          const isGroupCollapsed = collapsedGroups[groupId];

          return (
            <div
              className={groupContainerClasses}
              key={groupId}
              data-id={groupId}
            >
              <div
                className={labelContainerClasses}
                onClick={
                  viewOnly
                    ? null
                    : () => {
                        handleOnGroupClick({
                          groupId,
                          selectedGroupOptions: options,
                        });
                      }
                }
                data-test-id={`${componentTestId}-${groupId}-checkbox`}
              >
                <div className={labelClasses}>
                  {label}
                  <IconButton
                    dsVersion="2.0"
                    variant="neutral"
                    type="plain"
                    size="x-small"
                    icon={
                      isGroupCollapsed ? (
                        <CaretClosedFilled />
                      ) : (
                        <CaretOpenedFilled />
                      )
                    }
                    onClick={e => handleToggleCollapseList(e, groupId)}
                  />
                </div>
                {viewOnly ? null : (
                  <Checkbox
                    isChecked={
                      _.get(selectedGroupStatus, groupId) ===
                      CHECKBOX_STATE.DETERMINATE
                    }
                    isIndeterminate={
                      _.get(selectedGroupStatus, groupId) ===
                      CHECKBOX_STATE.INDETERMINATE
                    }
                    testId={`${componentTestId}-${label}`}
                  />
                )}
              </div>
              {isGroupCollapsed ? null : (
                <div className={classes.recipientSubItemsContainer}>
                  <MemoisedOptionList
                    viewOnly={viewOnly}
                    options={options}
                    groupId={groupId}
                    selectedItemListSet={selectedItemListSet}
                    setSelectedItemsList={setSelectedItemsList}
                    selectedUserTypes={selectedUserTypes}
                    testId={componentTestId}
                    coursesRef={coursesRef}
                    coursesSetRef={coursesSetRef}
                    countMapping={countMapping}
                    loadingCount={loadingCount}
                    isNotMultiNodeCount={isNotMultiNodeCount}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default memo(GroupedList);
